import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash, faEdit, faPlus, faSave, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import useFetch from '../../hooks/useFetch';

function SeasonCalendarManager() {
  const { fetchData, loading, error } = useFetch();
  const [seasons, setSeasons] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', startDate: '', endDate: '' });
  const [isCreating, setIsCreating] = useState(false);
  const [minStartDate, setMinStartDate] = useState('');

  const fetchSeasons = useCallback(async () => {
    try {
      const data = await fetchData('/api/admin/seasons');
      if (data) {
        // Sort by start date descending
        const sorted = data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
        setSeasons(sorted);
      }
    } catch (err) {
      console.error('Error fetching seasons:', err);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'startDate' && value) {
      const start = new Date(value);
      const end = new Date(start);
      end.setDate(end.getDate() + 28);
      const calculatedEndDate = end.toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, [name]: value, endDate: calculatedEndDate }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const startCreating = () => {
    setIsCreating(true);
    setEditingId(null);

    let nextStartDate = '';
    let nextEndDate = '';
    let minDate = '';

    if (seasons.length > 0) {
      const latestEndDate = seasons.reduce((max, season) => {
        const seasonEnd = new Date(season.endDate);
        return seasonEnd > max ? seasonEnd : max;
      }, new Date(0));

      const start = new Date(latestEndDate);
      start.setDate(start.getDate() + 1);
      [nextStartDate] = start.toISOString().split('T');
      minDate = nextStartDate;

      const end = new Date(start);
      end.setDate(end.getDate() + 28);
      [nextEndDate] = end.toISOString().split('T');
    }

    setMinStartDate(minDate);
    setFormData({ name: '', startDate: nextStartDate, endDate: nextEndDate });
  };

  const startEditing = (season) => {
    setIsCreating(false);
    setEditingId(season.id);
    setMinStartDate('');
    setFormData({
      name: season.name,
      startDate: season.startDate ? season.startDate.split('T')[0] : '',
      endDate: season.endDate ? season.endDate.split('T')[0] : '',
    });
  };

  const cancelEdit = () => {
    setIsCreating(false);
    setEditingId(null);
    setMinStartDate('');
    setFormData({ name: '', startDate: '', endDate: '' });
  };

  const saveSeason = async () => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      // eslint-disable-next-line no-alert
      alert('Please fill in all fields');
      return;
    }

    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    const fourWeeks = 28 * 24 * 60 * 60 * 1000;

    if (end - start < fourWeeks) {
      // eslint-disable-next-line no-alert
      alert('The end date of the season cannot be sooner than 4 weeks of the start date.');
      return;
    }

    if (isCreating && seasons.length > 0) {
      const latestEndDate = seasons.reduce((max, season) => {
        const seasonEnd = new Date(season.endDate);
        return seasonEnd > max ? seasonEnd : max;
      }, new Date(0));

      if (start <= latestEndDate) {
        // eslint-disable-next-line no-alert
        alert(`The start date cannot be earlier than the latest season's end date (${latestEndDate.toLocaleDateString()}).`);
        return;
      }
    }

    const url = isCreating ? '/api/admin/seasons' : `/api/admin/seasons/${editingId}`;
    const method = isCreating ? 'POST' : 'PUT';

    const result = await fetchData(url, { method, body: formData });
    if (result) {
      await fetchSeasons();
      cancelEdit();
    }
  };

  const deleteSeason = async (id) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Are you sure you want to delete this season?')) {
      await fetchData(`/api/admin/seasons/${id}`, { method: 'DELETE' });
      await fetchSeasons();
    }
  };

  return (
    <div className="container mx-auto p-4 text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Season Calendar Manager</h1>
        {!isCreating && !editingId && (
          <button
            type="button"
            onClick={startCreating}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded flex items-center"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Season
          </button>
        )}
      </div>

      {error && <div className="bg-red-500 p-4 rounded mb-4">{error.message || 'An error occurred'}</div>}
      {loading && <div>Loading...</div>}

      {(isCreating || editingId) && (
        <div className="bg-gray-700 p-4 rounded mb-6 border border-gray-600">
          <h2 className="text-xl font-semibold mb-4">{isCreating ? 'New Season' : 'Edit Season'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label htmlFor="name" className="block text-sm font-bold mb-2">
                Name
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded text-black"
                  placeholder="Season Name (e.g. Season 5)"
                />
              </label>
            </div>
            <div>
              <label htmlFor="startDate" className="block text-sm font-bold mb-2">
                Start Date
                <input
                  id="startDate"
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  min={isCreating ? minStartDate : undefined}
                  className="w-full p-2 rounded text-black"
                />
              </label>
            </div>
            <div>
              <label htmlFor="endDate" className="block text-sm font-bold mb-2">
                End Date
                <input
                  id="endDate"
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  className="w-full p-2 rounded text-black"
                />
              </label>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="bg-gray-500 hover:bg-gray-600 px-4 py-2 rounded flex items-center"
            >
              <FontAwesomeIcon icon={faTimes} className="mr-2" />
              {' '}
              Cancel
            </button>
            <button
              type="button"
              onClick={saveSeason}
              className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded flex items-center"
            >
              <FontAwesomeIcon icon={faSave} className="mr-2" />
              {' '}
              Save
            </button>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded shadow overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-700">
            <tr>
              <th className="p-4">Name</th>
              <th className="p-4">Start Date</th>
              <th className="p-4">End Date</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {seasons.length === 0 ? (
              <tr>
                <td colSpan="4" className="p-4 text-center text-gray-400">No seasons found.</td>
              </tr>
            ) : (
              seasons.map((season) => (
                <tr key={season.id} className="border-t border-gray-700 hover:bg-gray-700">
                  <td className="p-4">{season.name}</td>
                  <td className="p-4">{new Date(season.startDate).toLocaleDateString()}</td>
                  <td className="p-4">{new Date(season.endDate).toLocaleDateString()}</td>
                  <td className="p-4 text-right space-x-2">
                    <button
                      type="button"
                      onClick={() => startEditing(season)}
                      className="text-blue-400 hover:text-blue-300"
                      title="Edit"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteSeason(season.id)}
                      className="text-red-400 hover:text-red-300"
                      title="Delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default SeasonCalendarManager;
